"use client";

import { useState, useMemo, useCallback } from "react";
import { useTranslations } from "next-intl";
import { startOfDay, endOfDay } from "date-fns";
import { Calendar, ArrowRight } from "lucide-react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui";
import { cn } from "@/app/lib/utils";

export interface YearSelection {
   year: number;
   startDate: string;
   endDate: string;
}

export interface YearRangeSelection {
   startYear: number;
   endYear: number;
   startDate: string;
   endDate: string;
}

interface YearPickerBaseProps {
   placeholder?: string;
   className?: string;
   disabled?: boolean;
   minYear?: number;
   maxYear?: number;
}

interface SingleModeProps extends YearPickerBaseProps {
   mode?: "single";
   value?: number;
   onValueChange: (selection: YearSelection) => void;
}

interface RangeModeProps extends YearPickerBaseProps {
   mode: "range";
   value?: { startYear: number; endYear: number };
   onValueChange: (selection: YearRangeSelection) => void;
}

type YearPickerProps = SingleModeProps | RangeModeProps;

export function YearPicker(props: YearPickerProps) {
   const {
      placeholder,
      className,
      disabled = false,
      minYear = 2020,
      maxYear = new Date().getFullYear()
   } = props;

   const mode = props.mode ?? "single";
   const translate = useTranslations("Components.YearPicker");
   const [isOpen, setIsOpen] = useState(false);
   const [rangeStep, setRangeStep] = useState<"start" | "end">("start");
   const [tempStartYear, setTempStartYear] = useState<number | null>(null);
   const currentYear = new Date().getFullYear();

   const years = useMemo(() => {
      const yearList: number[] = [];
      for (let year = minYear; year <= maxYear; year++) {
         yearList.push(year);
      }
      return yearList;
   }, [minYear, maxYear]);

   const handleSingleYearSelect = useCallback(
      (year: number) => {
         if (mode !== "single") return;
         const startDate = startOfDay(new Date(year, 0, 1)).toISOString();
         const endDate = endOfDay(new Date(year, 11, 31)).toISOString();

         (props as SingleModeProps).onValueChange({
            year,
            startDate,
            endDate
         });
         setIsOpen(false);
      },
      [mode, props]
   );

   const handleRangeYearSelect = useCallback(
      (year: number) => {
         if (mode !== "range") return;

         if (rangeStep === "start") {
            setTempStartYear(year);
            setRangeStep("end");
         } else {
            const startYear = tempStartYear!;
            const endYear = year;
            const actualStart = Math.min(startYear, endYear);
            const actualEnd = Math.max(startYear, endYear);

            const startDate = startOfDay(new Date(actualStart, 0, 1)).toISOString();
            const endDate = endOfDay(new Date(actualEnd, 11, 31)).toISOString();

            (props as RangeModeProps).onValueChange({
               startYear: actualStart,
               endYear: actualEnd,
               startDate,
               endDate
            });
            setIsOpen(false);
            setRangeStep("start");
            setTempStartYear(null);
         }
      },
      [mode, rangeStep, tempStartYear, props]
   );

   const handleOpenChange = useCallback((open: boolean) => {
      setIsOpen(open);
      if (!open) {
         setRangeStep("start");
         setTempStartYear(null);
      }
   }, []);

   const displayValue = useMemo(() => {
      if (mode === "single") {
         const value = (props as SingleModeProps).value;
         return value ? value.toString() : null;
      } else {
         // When selecting end year, show temp start year with pending indicator
         if (rangeStep === "end" && tempStartYear) {
            return `${tempStartYear} - ?`;
         }
         const value = (props as RangeModeProps).value;
         if (value?.startYear && value?.endYear) {
            if (value.startYear === value.endYear) {
               return value.startYear.toString();
            }
            return `${value.startYear} - ${value.endYear}`;
         }
         return null;
      }
   }, [mode, props, rangeStep, tempStartYear]);

   const isYearSelected = useCallback(
      (year: number) => {
         if (mode === "single") {
            return (props as SingleModeProps).value === year;
         } else {
            // Don't show old selection when selecting new range
            if (rangeStep === "end" && tempStartYear) {
               return year === tempStartYear;
            }
            const value = (props as RangeModeProps).value;
            if (!value) return false;
            return year >= value.startYear && year <= value.endYear;
         }
      },
      [mode, props, rangeStep, tempStartYear]
   );

   const isYearInTempRange = useCallback(
      (year: number) => {
         if (mode !== "range" || !tempStartYear) return false;
         return year === tempStartYear;
      },
      [mode, tempStartYear]
   );

   return (
      <div className={cn("grid gap-2", className)}>
         <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
               <Button
                  variant="outline"
                  className={cn(
                     "w-full justify-start text-left font-normal",
                     !displayValue && "text-muted-foreground"
                  )}
                  disabled={disabled}
               >
                  <Calendar className="mr-2 h-4 w-4" />
                  {displayValue || placeholder || translate("selectYear")}
               </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start">
               {/* Header */}
               <div className="text-center mb-3">
                  {mode === "range" ? (
                     <div className="flex items-center justify-center gap-2">
                        <span
                           className={cn(
                              "text-sm font-medium",
                              rangeStep === "start"
                                 ? "text-primary"
                                 : "text-muted-foreground"
                           )}
                        >
                           {tempStartYear || translate("startYear")}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span
                           className={cn(
                              "text-sm font-medium",
                              rangeStep === "end"
                                 ? "text-primary"
                                 : "text-muted-foreground"
                           )}
                        >
                           {translate("endYear")}
                        </span>
                     </div>
                  ) : (
                     <span className="text-sm font-medium text-muted-foreground">
                        {minYear} - {maxYear}
                     </span>
                  )}
               </div>

               {/* Year grid */}
               <div className="grid grid-cols-3 gap-2">
                  {years.map((year) => (
                     <Button
                        key={year}
                        variant={
                           isYearSelected(year) || isYearInTempRange(year)
                              ? "default"
                              : "outline"
                        }
                        size="sm"
                        onClick={() =>
                           mode === "single"
                              ? handleSingleYearSelect(year)
                              : handleRangeYearSelect(year)
                        }
                        className={cn(
                           "text-sm",
                           year === currentYear &&
                              !isYearSelected(year) &&
                              !isYearInTempRange(year) &&
                              "border-primary",
                           isYearInTempRange(year) && "bg-primary/80"
                        )}
                     >
                        {year}
                     </Button>
                  ))}
               </div>

               {/* Range mode: Reset button */}
               {mode === "range" && tempStartYear && (
                  <div className="mt-3 text-center">
                     <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                           setRangeStep("start");
                           setTempStartYear(null);
                        }}
                     >
                        {translate("reset")}
                     </Button>
                  </div>
               )}
            </PopoverContent>
         </Popover>
      </div>
   );
}
